import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, ImagePlus, BarChart3, Shuffle, Calendar as CalIcon, Clock,
  Instagram, Youtube, Twitter, Facebook, MessageCircle, Send, Smartphone, Mail,
  Check, Copy, TrendingUp, TrendingDown, Minus, RefreshCw, Coins, ArrowRight,
  Zap, Target, Brain, ChevronDown, ChevronRight, PenSquare
} from 'lucide-react';

const CHANNEL_ICONS = {
  instagram: Instagram, youtube: Youtube, twitter: Twitter, facebook: Facebook,
  whatsapp: MessageCircle, telegram: Send, sms: Smartphone, email: Mail
};
const CHANNEL_COLORS = {
  instagram: '#E1306C', youtube: '#FF0033', twitter: '#A8B3BD', facebook: '#1877F2',
  whatsapp: '#22C55E', telegram: '#2AABEE', sms: '#84CC16', email: '#F59E0B'
};
const CHANNEL_NAMES = {
  instagram: 'Instagram', youtube: 'YouTube', twitter: 'X (Twitter)', facebook: 'Facebook',
  whatsapp: 'WhatsApp', telegram: 'Telegram', sms: 'SMS', email: 'E-posta'
};

const CATEGORIES = [
  { value: 'product', tr: '\u00dcr\u00fcn Tan\u0131t\u0131m\u0131', en: 'Product Promotion' },
  { value: 'campaign', tr: 'Kampanya', en: 'Campaign' },
  { value: 'educational', tr: 'E\u011fitim / Bilgi', en: 'Educational' },
  { value: 'entertainment', tr: 'E\u011flence', en: 'Entertainment' },
  { value: 'announcement', tr: 'Duyuru', en: 'Announcement' },
  { value: 'behind_scenes', tr: 'Sahne Arkas\u0131', en: 'Behind the Scenes' },
  { value: 'user_generated', tr: 'Kullan\u0131c\u0131 \u0130\u00e7eri\u011fi', en: 'User Generated' },
  { value: 'seasonal', tr: 'Sezonluk / \u00d6zel G\u00fcn', en: 'Seasonal / Holiday' },
];

const TONES = [
  { value: 'professional', tr: 'Profesyonel', en: 'Professional' },
  { value: 'casual', tr: 'Samimi', en: 'Casual' },
  { value: 'exciting', tr: 'Heyecanl\u0131', en: 'Exciting' },
  { value: 'informative', tr: 'Bilgilendirici', en: 'Informative' },
  { value: 'humorous', tr: 'Komik', en: 'Humorous' },
  { value: 'urgent', tr: 'Acil / S\u0131n\u0131rl\u0131 S\u00fcre', en: 'Urgent / Limited Time' },
  { value: 'inspirational', tr: '\u0130lham Verici', en: 'Inspirational' },
];

function PredictionBadge({ level }) {
  const config = {
    high: { color: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20', icon: TrendingUp },
    medium: { color: 'bg-amber-500/10 text-amber-300 border-amber-500/20', icon: Minus },
    low: { color: 'bg-red-500/10 text-red-300 border-red-500/20', icon: TrendingDown },
  };
  const c = config[level] || config.medium;
  return (
    <Badge className={`${c.color} border gap-1`}>
      <c.icon size={12} />
      <span className="capitalize">{level}</span>
    </Badge>
  );
}

function ScoreBar({ value, label, color = 'bg-[hsl(var(--primary))]' }) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-[hsl(var(--muted-foreground))]">{label}</span>
        <span className="tabular-nums font-medium">{value}%</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

export default function AIStudio() {
  const { i18n } = useTranslation();
  const { api, user, refreshUser } = useAuth();
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';

  // Content Generation
  const [category, setCategory] = useState('');
  const [tone, setTone] = useState('');
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [brandContext, setBrandContext] = useState('');
  const [generateImage, setGenerateImage] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);

  // Prediction
  const [predicting, setPredicting] = useState({});
  const [predictions, setPredictions] = useState({});

  // Variations
  const [variationContent, setVariationContent] = useState('');
  const [variationChannels, setVariationChannels] = useState([]);
  const [generatingVariations, setGeneratingVariations] = useState(false);
  const [variations, setVariations] = useState(null);

  // Weekly Plan
  const [planGoals, setPlanGoals] = useState('');
  const [planCategories, setPlanCategories] = useState([]);
  const [planChannels, setPlanChannels] = useState([]);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [weeklyPlan, setWeeklyPlan] = useState(null);

  // Best Times
  const [timesChannels, setTimesChannels] = useState([]);
  const [fetchingTimes, setFetchingTimes] = useState(false);
  const [bestTimes, setBestTimes] = useState(null);

  // Costs
  const [aiCosts, setAiCosts] = useState({});
  useEffect(() => {
    api.get('/api/ai/credit-costs').then(r => setAiCosts(r.data)).catch(() => {});
  }, [api]);

  const toggleChannel = (list, setList, ch) => {
    setList(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);
  };

  const handleGenerate = async () => {
    if (!category || !tone || selectedChannels.length === 0) {
      toast.error(lang === 'tr' ? 'Kategori, ton ve en az 1 kanal se\u00e7in' : 'Select category, tone and at least 1 channel');
      return;
    }
    setGenerating(true);
    setGeneratedContent(null);
    setGeneratedImage(null);
    setPredictions({});
    try {
      const res = await api.post('/api/ai/generate-content', {
        category, tone, channels: selectedChannels, brand_context: brandContext,
        language: lang, generate_image: generateImage
      });
      setGeneratedContent(res.data);
      if (res.data.image_base64) setGeneratedImage(res.data.image_base64);
      refreshUser();
      toast.success(lang === 'tr' ? `\u0130\u00e7erik \u00fcretildi! ${res.data.credits_used} kredi kullan\u0131ld\u0131` : `Content generated! ${res.data.credits_used} credits used`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handlePredict = async (channel, text, hashtags) => {
    setPredicting(p => ({ ...p, [channel]: true }));
    try {
      const res = await api.post('/api/ai/predict-performance', { channel, text, hashtags, language: lang });
      setPredictions(p => ({ ...p, [channel]: res.data }));
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Prediction failed');
    } finally {
      setPredicting(p => ({ ...p, [channel]: false }));
    }
  };

  const handleGenerateVariations = async () => {
    if (!variationContent.trim() || variationChannels.length === 0) {
      toast.error(lang === 'tr' ? '\u0130\u00e7erik ve kanal se\u00e7in' : 'Enter content and select channels');
      return;
    }
    setGeneratingVariations(true);
    setVariations(null);
    try {
      const res = await api.post('/api/ai/generate-variations', {
        content: variationContent, channels: variationChannels, language: lang
      });
      setVariations(res.data);
      refreshUser();
      toast.success(lang === 'tr' ? 'Varyasyonlar \u00fcretildi!' : 'Variations generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Variation generation failed');
    } finally {
      setGeneratingVariations(false);
    }
  };

  const handleGeneratePlan = async () => {
    if (!planGoals.trim() || planChannels.length === 0) {
      toast.error(lang === 'tr' ? 'Hedef ve kanal se\u00e7in' : 'Enter goals and select channels');
      return;
    }
    setGeneratingPlan(true);
    setWeeklyPlan(null);
    try {
      const cats = planCategories.length > 0 ? planCategories : ['product', 'educational', 'entertainment'];
      const res = await api.post('/api/ai/weekly-plan', {
        brand_goals: planGoals, categories: cats, channels: planChannels, language: lang
      });
      setWeeklyPlan(res.data);
      refreshUser();
      toast.success(lang === 'tr' ? 'Haftal\u0131k plan \u00fcretildi!' : 'Weekly plan generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Plan generation failed');
    } finally {
      setGeneratingPlan(false);
    }
  };

  const handleBestTimes = async () => {
    if (timesChannels.length === 0) {
      toast.error(lang === 'tr' ? 'En az 1 kanal se\u00e7in' : 'Select at least 1 channel');
      return;
    }
    setFetchingTimes(true);
    setBestTimes(null);
    try {
      const res = await api.post('/api/ai/best-times', { channels: timesChannels, language: lang });
      setBestTimes(res.data);
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Best times analysis failed');
    } finally {
      setFetchingTimes(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(lang === 'tr' ? 'Kopyaland\u0131!' : 'Copied!');
  };

  const goToPostCreator = (text, imageBase64) => {
    // Store in sessionStorage for PostCreator to pick up
    sessionStorage.setItem('ai_content', text);
    if (imageBase64) sessionStorage.setItem('ai_image', imageBase64);
    window.location.href = '/create';
  };

  const ChannelChips = ({ channels, selected, onToggle }) => (
    <div className="flex flex-wrap gap-2">
      {channels.map(ch => {
        const Icon = CHANNEL_ICONS[ch] || Sparkles;
        const isSelected = selected.includes(ch);
        return (
          <button key={ch} onClick={() => onToggle(ch)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition-all duration-200 ${
              isSelected ? 'bg-white/10 border-white/20' : 'border-white/5 hover:border-white/15 opacity-50 hover:opacity-80'
            }`} style={{ borderLeftColor: CHANNEL_COLORS[ch], borderLeftWidth: isSelected ? 3 : 1 }}>
            <Icon size={14} style={{ color: CHANNEL_COLORS[ch] }} />
            <span>{CHANNEL_NAMES[ch] || ch}</span>
          </button>
        );
      })}
    </div>
  );

  const allChannels = Object.keys(CHANNEL_ICONS);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Brain size={28} className="text-[hsl(var(--primary))]" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {lang === 'tr' ? 'AI Strateji Motoru' : 'AI Strategy Engine'}
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            {lang === 'tr' ? 'Yapay zeka destekli i\u00e7erik \u00fcretimi, performans tahmini ve planlama' : 'AI-powered content generation, performance prediction & planning'}
          </p>
        </div>
      </div>

      <Tabs defaultValue="generate" className="space-y-4">
        <TabsList className="bg-[hsl(var(--secondary))] flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="generate" data-testid="ai-tab-generate" className="gap-1.5">
            <Sparkles size={14} /> {lang === 'tr' ? '\u0130\u00e7erik \u00dcret' : 'Generate'}
          </TabsTrigger>
          <TabsTrigger value="variations" data-testid="ai-tab-variations" className="gap-1.5">
            <Shuffle size={14} /> {lang === 'tr' ? 'Varyasyonlar' : 'Variations'}
          </TabsTrigger>
          <TabsTrigger value="weekly" data-testid="ai-tab-weekly" className="gap-1.5">
            <CalIcon size={14} /> {lang === 'tr' ? 'Haftal\u0131k Plan' : 'Weekly Plan'}
          </TabsTrigger>
          <TabsTrigger value="times" data-testid="ai-tab-times" className="gap-1.5">
            <Clock size={14} /> {lang === 'tr' ? 'En \u0130yi Saatler' : 'Best Times'}
          </TabsTrigger>
        </TabsList>

        {/* ====== TAB: GENERATE CONTENT ====== */}
        <TabsContent value="generate" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left: Settings */}
            <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Target size={16} className="text-[hsl(var(--primary))]" />
                  {lang === 'tr' ? '\u0130\u00e7erik Ayarlar\u0131' : 'Content Settings'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs text-[hsl(var(--muted-foreground))]">{lang === 'tr' ? 'Kategori' : 'Category'}</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="bg-black/20 border-white/10" data-testid="ai-category">
                      <SelectValue placeholder={lang === 'tr' ? 'Kategori se\u00e7in' : 'Select category'} />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--popover))] border-white/10">
                      {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c[lang]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-[hsl(var(--muted-foreground))]">{lang === 'tr' ? 'Ton' : 'Tone'}</label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger className="bg-black/20 border-white/10" data-testid="ai-tone">
                      <SelectValue placeholder={lang === 'tr' ? 'Ton se\u00e7in' : 'Select tone'} />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--popover))] border-white/10">
                      {TONES.map(t => <SelectItem key={t.value} value={t.value}>{t[lang]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-[hsl(var(--muted-foreground))]">{lang === 'tr' ? 'Marka / \u00dcr\u00fcn A\u00e7\u0131klamas\u0131' : 'Brand / Product Context'}</label>
                  <Textarea value={brandContext} onChange={e => setBrandContext(e.target.value)}
                    className="bg-black/20 border-white/10 min-h-[60px]" data-testid="ai-brand-context"
                    placeholder={lang === 'tr' ? 'Markan\u0131z veya \u00fcr\u00fcn\u00fcn\u00fcz hakk\u0131nda k\u0131sa bilgi...' : 'Brief info about your brand or product...'} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-[hsl(var(--muted-foreground))]">{lang === 'tr' ? 'Kanallar' : 'Channels'}</label>
                  <ChannelChips channels={allChannels} selected={selectedChannels}
                    onToggle={(ch) => toggleChannel(selectedChannels, setSelectedChannels, ch)} />
                </div>
                <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                  <span className="text-xs">{lang === 'tr' ? 'G\u00f6rsel de \u00fcret (DALL\u00b7E)' : 'Also generate image (DALL\u00b7E)'}</span>
                  <Switch checked={generateImage} onCheckedChange={setGenerateImage} data-testid="ai-gen-image-toggle" />
                </div>
                <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                  <Coins size={14} className="text-[hsl(var(--primary))]" />
                  <span>{lang === 'tr' ? 'Maliyet:' : 'Cost:'} {aiCosts.content_generation || 3}{generateImage ? ` + ${5}` : ''} {lang === 'tr' ? 'kredi' : 'credits'}</span>
                </div>
                <Button onClick={handleGenerate} disabled={generating}
                  className="w-full gold-sheen bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" data-testid="ai-generate-btn">
                  {generating ? <Loader2 size={16} className="animate-spin mr-2" /> : <Sparkles size={16} className="mr-2" />}
                  {generating ? (lang === 'tr' ? '\u00dcretiliyor...' : 'Generating...') : (lang === 'tr' ? '\u0130\u00e7erik \u00dcret' : 'Generate Content')}
                </Button>
              </CardContent>
            </Card>

            {/* Right: Generated Content */}
            <div className="lg:col-span-2 space-y-4">
              {generatedContent && generatedContent.channels ? (
                <>
                  {generatedImage && (
                    <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
                      <CardContent className="p-3">
                        <img src={`data:image/png;base64,${generatedImage}`} alt="AI Generated"
                          className="w-full max-h-64 object-contain rounded-lg" data-testid="ai-generated-image" />
                      </CardContent>
                    </Card>
                  )}
                  {generatedContent.content_strategy_note && (
                    <div className="p-3 rounded-xl bg-[hsl(var(--accent))]/5 border border-[hsl(var(--accent))]/20 text-sm text-[hsl(var(--muted-foreground))]">
                      <Zap size={14} className="inline mr-1.5 text-[hsl(var(--accent))]" />
                      {generatedContent.content_strategy_note}
                    </div>
                  )}
                  {Object.entries(generatedContent.channels).map(([ch, data]) => {
                    const Icon = CHANNEL_ICONS[ch] || Sparkles;
                    const pred = predictions[ch];
                    const isPredicting = predicting[ch];
                    return (
                      <Card key={ch} className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80 border-l-[3px]"
                        style={{ borderLeftColor: CHANNEL_COLORS[ch] }}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                              <Icon size={18} style={{ color: CHANNEL_COLORS[ch] }} />
                              {CHANNEL_NAMES[ch] || ch}
                              {data.content_type && <Badge variant="outline" className="text-[10px] border-white/20">{data.content_type}</Badge>}
                            </CardTitle>
                            <div className="flex items-center gap-1.5">
                              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1"
                                onClick={() => copyToClipboard(data.text + '\n\n' + (data.hashtags || []).join(' '))}>
                                <Copy size={12} /> {lang === 'tr' ? 'Kopyala' : 'Copy'}
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1"
                                onClick={() => goToPostCreator(data.text + '\n\n' + (data.hashtags || []).join(' '), generatedImage)}>
                                <PenSquare size={12} /> {lang === 'tr' ? 'Kullan' : 'Use'}
                              </Button>
                              <Button variant="outline" size="sm" className="h-7 text-xs gap-1 border-white/10"
                                onClick={() => handlePredict(ch, data.text, data.hashtags)} disabled={isPredicting}
                                data-testid={`predict-${ch}`}>
                                {isPredicting ? <Loader2 size={12} className="animate-spin" /> : <BarChart3 size={12} />}
                                {lang === 'tr' ? 'Tahmin' : 'Predict'}
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{data.text}</p>
                          {data.hashtags && data.hashtags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {data.hashtags.map((tag, i) => (
                                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">{tag}</span>
                              ))}
                            </div>
                          )}
                          {data.image_prompt && (
                            <div className="p-2 rounded-lg bg-white/5 border border-white/5">
                              <p className="text-[10px] text-[hsl(var(--muted-foreground))] mb-1">
                                <ImagePlus size={10} className="inline mr-1" />
                                {lang === 'tr' ? 'G\u00f6rsel \u00d6nerisi:' : 'Image Prompt:'}
                              </p>
                              <p className="text-xs text-[hsl(var(--muted-foreground))]">{data.image_prompt}</p>
                            </div>
                          )}
                          {/* Prediction Results */}
                          {pred && (
                            <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">{lang === 'tr' ? 'Performans Tahmini' : 'Performance Prediction'}</span>
                                <PredictionBadge level={pred.engagement_potential} />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <ScoreBar value={pred.reach_score || 50} label={lang === 'tr' ? 'Eri\u015fim' : 'Reach'} color="bg-[hsl(var(--primary))]" />
                                <ScoreBar value={pred.virality_score || 30} label={lang === 'tr' ? 'Virallik' : 'Virality'} color="bg-[hsl(var(--accent))]" />
                              </div>
                              {pred.analysis && (
                                <div className="grid grid-cols-2 gap-3">
                                  <ScoreBar value={pred.analysis.text_quality || 50} label={lang === 'tr' ? 'Metin Kalitesi' : 'Text Quality'} color="bg-sky-500" />
                                  <ScoreBar value={pred.analysis.call_to_action_strength || 50} label="CTA" color="bg-amber-500" />
                                </div>
                              )}
                              {pred.rationale && (
                                <p className="text-xs text-[hsl(var(--muted-foreground))] italic">{pred.rationale}</p>
                              )}
                              {pred.improvements && pred.improvements.length > 0 && (
                                <div>
                                  <p className="text-[10px] text-[hsl(var(--muted-foreground))] mb-1">{lang === 'tr' ? '\u0130yile\u015ftirme \u00d6nerileri:' : 'Improvements:'}</p>
                                  {pred.improvements.map((imp, i) => (
                                    <p key={i} className="text-xs text-amber-300/80 flex items-start gap-1"><ArrowRight size={10} className="mt-0.5 shrink-0" />{imp}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </>
              ) : (
                <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80 flex items-center justify-center min-h-[300px]">
                  <div className="text-center p-8">
                    <Sparkles size={48} className="mx-auto mb-4 text-[hsl(var(--primary))]/20" />
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                      {lang === 'tr' ? 'Kategori, ton ve kanallar\u0131 se\u00e7ip "\u0130\u00e7erik \u00dcret" butonuna t\u0131klay\u0131n'
                        : 'Select category, tone, channels and click "Generate Content"'}
                    </p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ====== TAB: VARIATIONS ====== */}
        <TabsContent value="variations" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Shuffle size={16} className="text-[hsl(var(--accent))]" />
                  {lang === 'tr' ? 'Varyasyon \u00dcret' : 'Generate Variations'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs text-[hsl(var(--muted-foreground))]">{lang === 'tr' ? 'Orijinal \u0130\u00e7erik' : 'Original Content'}</label>
                  <Textarea value={variationContent} onChange={e => setVariationContent(e.target.value)}
                    className="bg-black/20 border-white/10 min-h-[120px]" data-testid="variation-content"
                    placeholder={lang === 'tr' ? 'Varyasyonlar\u0131 \u00fcretmek istedi\u011finiz mevcut i\u00e7eri\u011fi yap\u0131\u015ft\u0131r\u0131n...' : 'Paste existing content to generate variations...'} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-[hsl(var(--muted-foreground))]">{lang === 'tr' ? 'Kanallar' : 'Channels'}</label>
                  <ChannelChips channels={allChannels} selected={variationChannels}
                    onToggle={(ch) => toggleChannel(variationChannels, setVariationChannels, ch)} />
                </div>
                <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                  <Coins size={14} className="text-[hsl(var(--primary))]" />
                  <span>{lang === 'tr' ? 'Maliyet:' : 'Cost:'} {aiCosts.variation_generation || 2} {lang === 'tr' ? 'kredi' : 'credits'}</span>
                </div>
                <Button onClick={handleGenerateVariations} disabled={generatingVariations}
                  className="w-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" data-testid="generate-variations-btn">
                  {generatingVariations ? <Loader2 size={16} className="animate-spin mr-2" /> : <Shuffle size={16} className="mr-2" />}
                  {generatingVariations ? (lang === 'tr' ? '\u00dcretiliyor...' : 'Generating...') : (lang === 'tr' ? 'Varyasyonlar \u00dcret' : 'Generate Variations')}
                </Button>
              </CardContent>
            </Card>
            <div className="lg:col-span-2 space-y-3">
              {variations && variations.variations ? (
                variations.variations.map((v, vi) => (
                  <Card key={vi} className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] border-[hsl(var(--accent))]/30 text-[hsl(var(--accent))]">{v.label || v.type}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {v.channels && Object.entries(v.channels).map(([ch, chData]) => {
                        const Icon = CHANNEL_ICONS[ch] || Sparkles;
                        return (
                          <div key={ch} className="p-3 rounded-lg border border-white/5 bg-white/[0.02] border-l-2" style={{ borderLeftColor: CHANNEL_COLORS[ch] }}>
                            <div className="flex items-center gap-2 mb-1">
                              <Icon size={14} style={{ color: CHANNEL_COLORS[ch] }} />
                              <span className="text-xs font-medium">{CHANNEL_NAMES[ch]}</span>
                              <Button variant="ghost" size="sm" className="h-6 text-[10px] ml-auto gap-1" onClick={() => copyToClipboard(chData.text)}>
                                <Copy size={10} /> {lang === 'tr' ? 'Kopyala' : 'Copy'}
                              </Button>
                            </div>
                            <p className="text-sm">{chData.text}</p>
                            {chData.cta && <p className="text-xs text-[hsl(var(--primary))] mt-1">CTA: {chData.cta}</p>}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80 flex items-center justify-center min-h-[250px]">
                  <div className="text-center p-8">
                    <Shuffle size={48} className="mx-auto mb-4 text-[hsl(var(--accent))]/20" />
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                      {lang === 'tr' ? 'Mevcut bir i\u00e7erinizi yap\u0131\u015ft\u0131r\u0131n ve farkl\u0131 versiyonlar \u00fcretin' : 'Paste existing content to generate different versions'}
                    </p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ====== TAB: WEEKLY PLAN ====== */}
        <TabsContent value="weekly" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <CalIcon size={16} className="text-sky-400" />
                  {lang === 'tr' ? 'Plan Ayarlar\u0131' : 'Plan Settings'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs text-[hsl(var(--muted-foreground))]">{lang === 'tr' ? 'Marka Hedefleri' : 'Brand Goals'}</label>
                  <Textarea value={planGoals} onChange={e => setPlanGoals(e.target.value)}
                    className="bg-black/20 border-white/10 min-h-[80px]" data-testid="plan-goals"
                    placeholder={lang === 'tr' ? 'Haftal\u0131k i\u00e7erik hedeflerinizi yaz\u0131n...' : 'Describe your weekly content goals...'} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-[hsl(var(--muted-foreground))]">{lang === 'tr' ? 'Kanallar' : 'Channels'}</label>
                  <ChannelChips channels={allChannels} selected={planChannels}
                    onToggle={(ch) => toggleChannel(planChannels, setPlanChannels, ch)} />
                </div>
                <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                  <Coins size={14} className="text-[hsl(var(--primary))]" />
                  <span>{lang === 'tr' ? 'Maliyet:' : 'Cost:'} {aiCosts.weekly_plan || 5} {lang === 'tr' ? 'kredi' : 'credits'}</span>
                </div>
                <Button onClick={handleGeneratePlan} disabled={generatingPlan}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white" data-testid="generate-plan-btn">
                  {generatingPlan ? <Loader2 size={16} className="animate-spin mr-2" /> : <CalIcon size={16} className="mr-2" />}
                  {generatingPlan ? (lang === 'tr' ? '\u00dcretiliyor...' : 'Generating...') : (lang === 'tr' ? 'Haftal\u0131k Plan \u00dcret' : 'Generate Weekly Plan')}
                </Button>
              </CardContent>
            </Card>
            <div className="lg:col-span-3">
              {weeklyPlan && weeklyPlan.plan ? (
                <div className="space-y-3">
                  {weeklyPlan.strategy_notes && (
                    <div className="p-3 rounded-xl bg-sky-500/5 border border-sky-500/20 text-sm text-[hsl(var(--muted-foreground))]">
                      <Zap size={14} className="inline mr-1.5 text-sky-400" />
                      {weeklyPlan.strategy_notes}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {weeklyPlan.plan.map((day, di) => (
                      <Card key={di} className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">{day.day}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {(day.items || []).map((item, ii) => {
                            const Icon = CHANNEL_ICONS[item.channel] || Sparkles;
                            return (
                              <div key={ii} className="p-2 rounded-lg border border-white/5 bg-white/[0.02] border-l-2" style={{ borderLeftColor: CHANNEL_COLORS[item.channel] || '#666' }}>
                                <div className="flex items-center gap-1.5 mb-1">
                                  <Icon size={12} style={{ color: CHANNEL_COLORS[item.channel] }} />
                                  <span className="text-[10px] font-medium">{CHANNEL_NAMES[item.channel]}</span>
                                  {item.suggested_time && <span className="text-[10px] text-[hsl(var(--muted-foreground))] ml-auto">{item.suggested_time}</span>}
                                </div>
                                <p className="text-xs font-medium">{item.title}</p>
                                <Badge variant="outline" className="text-[9px] border-white/10 mt-1">{item.content_type}</Badge>
                              </div>
                            );
                          })}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : (
                <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80 flex items-center justify-center min-h-[300px]">
                  <div className="text-center p-8">
                    <CalIcon size={48} className="mx-auto mb-4 text-sky-400/20" />
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                      {lang === 'tr' ? 'Hedeflerinizi ve kanallar\u0131n\u0131z\u0131 belirleyin, AI haftal\u0131k plan \u00f6nersin'
                        : 'Set your goals and channels, AI will suggest a weekly plan'}
                    </p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ====== TAB: BEST TIMES ====== */}
        <TabsContent value="times" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Clock size={16} className="text-amber-400" />
                  {lang === 'tr' ? 'Kanal Se\u00e7' : 'Select Channels'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ChannelChips channels={allChannels} selected={timesChannels}
                  onToggle={(ch) => toggleChannel(timesChannels, setTimesChannels, ch)} />
                <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                  <Coins size={14} className="text-[hsl(var(--primary))]" />
                  <span>{lang === 'tr' ? 'Maliyet:' : 'Cost:'} {aiCosts.best_times || 1} {lang === 'tr' ? 'kredi' : 'credits'}</span>
                </div>
                <Button onClick={handleBestTimes} disabled={fetchingTimes}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="best-times-btn">
                  {fetchingTimes ? <Loader2 size={16} className="animate-spin mr-2" /> : <Clock size={16} className="mr-2" />}
                  {fetchingTimes ? (lang === 'tr' ? 'Analiz ediliyor...' : 'Analyzing...') : (lang === 'tr' ? 'En \u0130yi Saatleri Bul' : 'Find Best Times')}
                </Button>
              </CardContent>
            </Card>
            <div className="lg:col-span-3">
              {bestTimes && bestTimes.channels ? (
                <div className="space-y-3">
                  {bestTimes.general_tip && (
                    <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 text-sm text-[hsl(var(--muted-foreground))]">
                      <Clock size={14} className="inline mr-1.5 text-amber-400" />
                      {bestTimes.general_tip}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(bestTimes.channels).map(([ch, data]) => {
                      const Icon = CHANNEL_ICONS[ch] || Clock;
                      return (
                        <Card key={ch} className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80 border-l-[3px]" style={{ borderLeftColor: CHANNEL_COLORS[ch] }}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                              <Icon size={18} style={{ color: CHANNEL_COLORS[ch] }} />
                              {CHANNEL_NAMES[ch]}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {data.best_days && (
                              <div>
                                <p className="text-[10px] text-[hsl(var(--muted-foreground))] mb-1">{lang === 'tr' ? 'En \u0130yi G\u00fcnler' : 'Best Days'}</p>
                                <div className="flex flex-wrap gap-1">
                                  {data.best_days.map((d, i) => (
                                    <Badge key={i} className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[10px]">{d}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {data.best_times && data.best_times.map((t, ti) => (
                              <div key={ti} className="p-2 rounded-lg bg-white/5 border border-white/5">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium">{t.hours}</span>
                                  <Badge variant="outline" className={`text-[9px] ${
                                    t.confidence === 'high' ? 'border-emerald-500/30 text-emerald-300'
                                    : t.confidence === 'medium' ? 'border-amber-500/30 text-amber-300'
                                    : 'border-white/20'
                                  }`}>{t.confidence}</Badge>
                                </div>
                                {t.reason && <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">{t.reason}</p>}
                              </div>
                            ))}
                            {data.tip && <p className="text-xs text-[hsl(var(--muted-foreground))] italic">{data.tip}</p>}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80 flex items-center justify-center min-h-[250px]">
                  <div className="text-center p-8">
                    <Clock size={48} className="mx-auto mb-4 text-amber-400/20" />
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                      {lang === 'tr' ? 'Kanallar\u0131 se\u00e7in ve AI en iyi payla\u015f\u0131m saatlerini \u00f6nersin'
                        : 'Select channels and AI will recommend the best posting times'}
                    </p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
